import React, { useState } from "react";
import { Plus, Loader2 } from "lucide-react";
import { Modal, FieldLabel, inputCls, InlineMsg } from "./shared";
import { createCustomKey } from "../../lib/integrationsApi";

export default function CustomKeyModal({ categories, defaultCategory, actor, onClose, onSaved }) {
  const [form, setForm] = useState({ label: "", category: defaultCategory || "other", value: "", description: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const valid = form.label.trim().length >= 2 && form.value.trim();

  async function submit(e) {
    e.preventDefault();
    if (!valid || busy) return;
    setBusy(true); setError(null);
    try {
      await createCustomKey({ ...form, label: form.label.trim(), value: form.value.trim() }, actor);
      onSaved();
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title="Add custom key" subtitle="For any service not in the built-in catalog"
           icon={Plus} onClose={onClose} busy={busy} testid="custom-key-modal">
      <form onSubmit={submit} className="p-5 space-y-3.5">
        <FieldLabel label="Service name">
          <input data-testid="custom-key-label" value={form.label} onChange={set("label")} maxLength={60}
                 placeholder="e.g. Viber Business API" className={inputCls} disabled={busy} />
        </FieldLabel>
        <FieldLabel label="Category">
          <select data-testid="custom-key-category" value={form.category} onChange={set("category")}
                  className={inputCls} disabled={busy}>
            {categories.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
        </FieldLabel>
        <FieldLabel label="Key value">
          <input data-testid="custom-key-value" type="password" value={form.value} onChange={set("value")}
                 autoComplete="new-password" spellCheck="false" className={inputCls} disabled={busy} />
        </FieldLabel>
        <FieldLabel label="Notes (optional)">
          <input data-testid="custom-key-description" value={form.description} onChange={set("description")}
                 maxLength={240} placeholder="What uses this key?" className={inputCls} disabled={busy} />
        </FieldLabel>
        {error && <InlineMsg testid="custom-key-error">{error}</InlineMsg>}
        <div className="flex items-center justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} disabled={busy} data-testid="custom-key-cancel"
                  className="px-3 py-1.5 text-[11.5px] border border-border rounded-sm hover:bg-surface-2 disabled:opacity-40">
            Cancel
          </button>
          <button type="submit" disabled={!valid || busy} data-testid="custom-key-save"
                  className="px-4 py-1.5 text-[11.5px] font-semibold bg-primary text-primary-fg hover:opacity-90 rounded-sm inline-flex items-center gap-1 disabled:opacity-40">
            {busy ? <Loader2 size={11} className="animate-spin" /> : <Plus size={11} />}
            {busy ? "Saving…" : "Add key"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
